import axios from 'axios';
import { db } from '../firebase';
import { doc, updateDoc, setDoc, collection, addDoc } from 'firebase/firestore';

const ContentModerationService = {
  // The OpenAI API key should be stored securely on the server-side
  // For development, we'll use an environment variable
  API_KEY: process.env.REACT_APP_OPENAI_API_KEY,
  API_URL: 'https://api.openai.com/v1/chat/completions',
  
  /**
   * Moderates study material to check if it's educational content
   * @param {string} contentText - Text extracted from the material
   * @param {string} title - Title of the material
   * @param {string} description - Optional description
   * @returns {Promise<{approved: boolean, reason: string}>}
   */
  async moderateMaterial(contentText, title, description = '') {
    try {
      // Sample text from the material (first 1000 chars)
      const sampleText = contentText.slice(0, 1000);
      
      const prompt = `
      You are a content moderator for an educational platform. Analyze this study material:
      
      Title: ${title}
      Description: ${description}
      Content sample: ${sampleText}
      
      Determine if this is educational content appropriate for a student learning platform.
      
      Your response must be in JSON format with two fields:
      {
        "approved": boolean, // true if educational, false if not
        "reason": string // brief explanation of the decision
      }
      
      Only return the JSON object, nothing else.
      `;
      
      return await this._makeOpenAIRequest(prompt);
    } catch (error) {
      console.error('Error in material moderation:', error);
      // Default to requiring manual review when errors occur
      return { 
        approved: false, 
        reason: 'Error during automated review. Requires manual review.' 
      };
    }
  },
  
  /**
   * Moderates a question to check if it's education-related
   * @param {string} questionText - The question text
   * @param {string} category - Optional category of the question
   * @returns {Promise<{approved: boolean, reason: string}>}
   */
  async moderateQuestion(questionText, category = '') {
    try {
      const prompt = `
      You are a content moderator for an educational Q&A platform. Analyze this question:
      
      Category: ${category}
      Question: ${questionText}
      
      Determine if this is an educational question appropriate for a student learning platform.
      Questions about homework, exams, learning, academic subjects, career guidance, and student life ARE allowed.
      Questions that are inappropriate, spam, or completely unrelated to education should be rejected.
      
      Your response must be in JSON format with two fields:
      {
        "approved": boolean, // true if educational, false if not
        "reason": string // brief explanation of the decision
      }
      
      Only return the JSON object, nothing else.
      `;
      
      return await this._makeOpenAIRequest(prompt);
    } catch (error) {
      console.error('Error in question moderation:', error);
      // Default to requiring manual review when errors occur
      return { 
        approved: false, 
        reason: 'Error during automated review. Requires manual review.' 
      };
    }
  },
  
  /**
   * Generic method to extract text from uploaded files
   * This is a simplified version, in production you would need more robust text extraction
   * based on file types (PDF, DOC, etc.)
   * @param {File} file 
   * @returns {Promise<string>}
   */
  async extractTextFromFile(file) {
    return new Promise((resolve, reject) => {
      // For text files
      if (file.type === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      } 
      // For PDFs, DOCs, etc., you would need specialized libraries
      // This is a placeholder that just extracts the filename and type
      else {
        resolve(`File: ${file.name}, Type: ${file.type}`);
      }
    });
  },
  
  /**
   * Logs moderation decisions to Firebase for admin review
   * @param {string} contentType - 'material' or 'question'
   * @param {string} contentId - ID of the content
   * @param {boolean} approved - Whether it was approved
   * @param {string} reason - Reason for the decision
   * @param {string} userId - User who submitted the content
   */
  async logModerationDecision(contentType, contentId, approved, reason, userId) {
    try {
      await addDoc(collection(db, 'moderationLogs'), {
        contentType,
        contentId,
        approved,
        reason,
        userId,
        timestamp: new Date().toISOString(),
        reviewStatus: 'auto' // vs 'manual' for admin reviews
      });
    } catch (error) {
      console.error('Error logging moderation decision:', error);
    }
  },
  
  /**
   * Makes a request to the OpenAI API
   * @param {string} prompt - The prompt to send to the API
   * @returns {Promise<Object>} - Parsed JSON response
   * @private
   */
  async _makeOpenAIRequest(prompt) {
    try {
      // For development/testing when API key isn't available
      if (!this.API_KEY) {
        console.warn('Using mock API response as no API key is provided');
        // 80% chance of approval for testing
        const mockApproved = Math.random() > 0.2;
        return {
          approved: mockApproved,
          reason: mockApproved 
            ? 'Content appears to be educational and appropriate.' 
            : 'Content does not appear to be educational in nature.'
        };
      }
      
      const response = await axios.post(
        this.API_URL,
        {
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: 'You are a content moderation assistant that analyzes educational content.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.2, // Lower temperature for more consistent responses
          max_tokens: 150
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.API_KEY}`
          }
        }
      );
      
      // Parse the JSON response from the API
      const responseText = response.data.choices[0].message.content.trim();
      return JSON.parse(responseText);
    } catch (error) {
      console.error('Error making OpenAI request:', error);
      throw error;
    }
  }
};

export default ContentModerationService; 